/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextField, Button, Grid, Snackbar, Alert } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory, useLocation } from "react-router-dom";

import { useAppContext } from "../core/AppContext";
import * as Yup from "yup";
import { useSiteService } from "../sites/siteService";
import { useTenantService } from "../tenants/tenantService";
import { AppName, AppRoleValues, BITMESH_TENANT } from "../core/AppConfig";
import { initUserProfileService, UserProfile } from "../security/userProfileService";
import { FormContainer, FormHeader, FormSection } from "../shared/FormLayout";
import { ProfileAccessFields } from "./ProfileAccessFields";
import { ProfileAccounts } from "./ProfileAccounts";

type Props = {
  profile: UserProfile | null;
};

const sortByName = (items: any[]) => items.sort((a: any, b: any) => a.name.localeCompare(b.name));

export const EditProfileForm: FC<Props> = ({ profile }) => {
  const app = useAppContext();
  const profileService = initUserProfileService();
  const siteService = useSiteService();
  const tenantService = useTenantService();
  const history = useHistory();
  // Set when navigating here from an account (ProfilePicker / AccountCard).
  const backTo = (useLocation().state as { from?: string } | undefined)?.from;

  const [successOpen, setSuccessOpen] = useState<boolean>(false);

  const [tenants, setTenants] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [siteOptions, setSiteOptions] = useState<any[]>([]);

  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [selectedSites, setSelectedSites] = useState<any[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<any[]>([]);
  const [fullAccessTenants, setFullAccessTenants] = useState<any[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<any[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  useEffect(() => {
    tenantService.getAll().then(tenants => setTenants(sortByName(tenants)));
    siteService.getAll().then(sites => {
      const sortedSites = sortByName(sites);
      setSites(sortedSites);
      setSiteOptions(sortedSites);
    });
  }, [app.user]);

  useEffect(() => {
    if (profile) {
      setIsPublic(profile?.isPublic ?? false);
      setFullAccessTenants(profile?.fullAccessTenantIds ?? []);
      setSelectedTenants(profile?.tenantIds ?? []);
      setSelectedSites(profile?.siteIds ?? []);
      setSelectedPermissions(profile?.permissions ?? []);
      setSelectedApplications(profile?.roles?.map(role => {
        return (Object.keys(AppRoleValues) as AppName[]).find((appName: AppName) => AppRoleValues[appName] === role) ?? '';
      }) ?? []);
    }
  }, [profile]);

  useEffect(() => {
    // Filter sites based on selected tenants
    // If Bitmesh tenant is selected, show all sites
    // Otherwise, only show sites belonging to selected tenants
    if (selectedTenants.length === 0) {
      setSiteOptions(sites);
    } else {
      const filteredSites = sites.filter(site =>
        selectedTenants.includes(BITMESH_TENANT) || selectedTenants.includes(site.tenantId)
      );
      setSiteOptions(sortByName(filteredSites));
    }
  }, [selectedTenants, sites]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Visningsnavn er påkrevd"),
  });
  const formOptions = {
    defaultValues: {
      name: profile?.name ?? "",
    },
    resolver: yupResolver(validationSchema),
  };

  // get functions to build form with useForm() hook
  const { register, handleSubmit, formState } =
    useForm(formOptions);
  const { errors, isSubmitting } = formState;

  async function onSubmit({ name }: any) {
    if (!profile) {
      await profileService.addUserProfile({
        name,
        siteIds: selectedSites,
        tenantIds: selectedTenants,
        fullAccessTenantIds: fullAccessTenants,
        isPublic,
        permissions: selectedPermissions,
        roles: selectedApplications.map((appName: AppName) => AppRoleValues[appName]),
      });
    } else {
      await profileService.updateUserProfile({
        id: profile?.id,
        name,
        siteIds: selectedSites,
        tenantIds: selectedTenants,
        fullAccessTenantIds: fullAccessTenants,
        isPublic,
        permissions: selectedPermissions,
        roles: selectedApplications.map((appName: AppName) => AppRoleValues[appName]),
      });
    }
    setSuccessOpen(true);
  }

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <FormHeader section="Profiler" title={profile?.name || "Ny profil"} isSubmitting={isSubmitting}>
        {backTo && (
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push(backTo)}>
            Tilbake til konto
          </Button>
        )}
      </FormHeader>

      <FormSection title="Profil">
        <Grid item xs={12} md={6}>
          <TextField
            label="Visningsnavn"
            size="small"
            fullWidth
            {...register("name")}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        </Grid>
      </FormSection>

      <ProfileAccessFields
        tenants={tenants}
        sites={sites}
        siteOptions={siteOptions}
        selectedApplications={selectedApplications}
        setSelectedApplications={setSelectedApplications}
        fullAccessTenants={fullAccessTenants}
        setFullAccessTenants={setFullAccessTenants}
        selectedTenants={selectedTenants}
        setSelectedTenants={setSelectedTenants}
        selectedSites={selectedSites}
        setSelectedSites={setSelectedSites}
        isPublic={isPublic}
        setIsPublic={setIsPublic}
        selectedPermissions={selectedPermissions}
        setSelectedPermissions={setSelectedPermissions}
      />

      {profile && <ProfileAccounts profileId={profile.id} />}

      <Snackbar open={successOpen} autoHideDuration={3000} onClose={() => setSuccessOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: '100%' }}>
          Profilen er {!profile ? 'opprettet' : 'oppdatert'}.
        </Alert>
      </Snackbar>
    </FormContainer>
  );
};
